import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, ButtonGroup, Card, Col, Form, Pagination, Row, Table, ToggleButton,
} from 'react-bootstrap';
import cashSessionService from '../services/cashSessionService';
import CashCombinedReport from './CashCombinedReport';
import CashSessionDetailModal from './CashSessionDetailModal';
import { businessToday, businessDateOffset } from '../utils/dateFilters';

const DEFAULT_PAGINATION = {
  page: 1, pageSize: 50, totalItems: 0, totalPages: 1,
  hasPreviousPage: false, hasNextPage: false,
};

const fmt = (value) => Number(value || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function buildPageNumbers(currentPage, totalPages) {
  const pages = new Set([1, totalPages]);
  for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
    if (page >= 1 && page <= totalPages) pages.add(page);
  }
  return Array.from(pages).sort((a, b) => a - b);
}

function initialFilter() {
  const today = businessToday();
  return { quickRange: 'TODAY', dateFrom: today, dateTo: today };
}

export default function CashHistoryPanel({
  scope = 'mine', userId = null, title = 'Historique des sessions clôturées',
}) {
  const isManagement = scope === 'user';
  const [viewMode, setViewMode] = useState('SEPARATE');
  const [filter, setFilter] = useState(initialFilter);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [combinedReport, setCombinedReport] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [detailShow, setDetailShow] = useState(false);
  const [detailSession, setDetailSession] = useState(null);

  useEffect(() => {
    setRows([]); setPagination(DEFAULT_PAGINATION); setCombinedReport(null);
    setLoaded(false); setErrorMsg(''); setFilter(initialFilter());
  }, [userId]);

  const pageNumbers = useMemo(
    () => buildPageNumbers(pagination.page, pagination.totalPages),
    [pagination.page, pagination.totalPages]
  );

  const buildParams = ({ page = 1, pageSize = pagination.pageSize, paginated = true } = {}) => {
    const params = {};
    if (filter.quickRange) params.quickRange = filter.quickRange;
    else {
      if (filter.dateFrom) params.dateFrom = filter.dateFrom;
      if (filter.dateTo) params.dateTo = filter.dateTo;
    }
    if (paginated) { params.page = page; params.pageSize = pageSize; }
    return params;
  };

  const validateFilter = () => {
    if (filter.dateFrom && filter.dateTo && filter.dateFrom > filter.dateTo) {
      setErrorMsg('La date de début ne peut pas être supérieure à la date de fin.');
      return false;
    }
    return true;
  };

  const loadSeparate = async (page = 1, pageSize = pagination.pageSize) => {
    if (!validateFilter() || (isManagement && !userId)) return;
    setLoading(true); setErrorMsg('');
    try {
      const params = buildParams({ page, pageSize, paginated: true });
      const response = isManagement
        ? await cashSessionService.userHistory(userId, params)
        : await cashSessionService.myHistory(params);
      if (Array.isArray(response)) {
        setRows(response);
        setPagination({ ...DEFAULT_PAGINATION, page, pageSize, totalItems: response.length });
      } else {
        setRows(Array.isArray(response?.data) ? response.data : []);
        setPagination({ ...DEFAULT_PAGINATION, ...(response?.pagination || {}) });
      }
      setCombinedReport(null); setLoaded(true);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || 'Erreur lors du chargement de l’historique.');
    } finally { setLoading(false); }
  };

  const loadCombined = async () => {
    if (!validateFilter() || (isManagement && !userId)) return;
    setLoading(true); setErrorMsg('');
    try {
      const params = buildParams({ paginated: false });
      const response = isManagement
        ? await cashSessionService.userCombinedHistory(userId, params)
        : await cashSessionService.myCombinedHistory(params);
      setCombinedReport(response || null); setRows([]); setLoaded(true);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || 'Erreur lors de la génération du rapport combiné.');
    } finally { setLoading(false); }
  };

  const runSearch = () => viewMode === 'COMBINED' ? loadCombined() : loadSeparate(1, pagination.pageSize);
  const changePageSize = (event) => {
    const nextSize = Number(event.target.value) || 50;
    setPagination((previous) => ({ ...previous, pageSize: nextSize }));
    if (loaded && viewMode === 'SEPARATE') loadSeparate(1, nextSize);
  };
  const openDetails = async (sessionId) => {
    setErrorMsg('');
    try {
      const session = await cashSessionService.get(sessionId);
      setDetailSession(session); setDetailShow(true);
    } catch (error) {
      setErrorMsg(error?.response?.data?.message || 'Erreur lors du chargement du détail de la session.');
    }
  };

  const handleQuickRange = (value) => {
    if (value === 'TODAY') {
      const today = businessToday();
      setFilter({ quickRange: 'TODAY', dateFrom: today, dateTo: today });
    } else if (value === 'YESTERDAY') {
      const yesterday = businessDateOffset(-1);
      setFilter({ quickRange: 'YESTERDAY', dateFrom: yesterday, dateTo: yesterday });
    } else {
      setFilter((previous) => ({ ...previous, quickRange: '' }));
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div><h5 className="mb-1">{title}</h5><div className="text-muted small">Aucun rapport n’est masqué : les rapports séparés sont chargés page par page et le rapport combiné utilise toutes les sessions de la période.</div></div>
          <Button variant="primary" onClick={runSearch} disabled={loading || (isManagement && !userId)}>{loading ? 'Chargement…' : viewMode === 'COMBINED' ? 'Générer' : 'Rechercher'}</Button>
        </div>
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        <Row className="g-3 align-items-end mb-3">
          <Col lg={4}>
            <Form.Label>Type de visualisation</Form.Label>
            <ButtonGroup className="d-flex">
              <ToggleButton id={`cash-history-separate-${isManagement ? userId : 'mine'}`} type="radio" variant="outline-primary" name={`cash-history-mode-${isManagement ? userId : 'mine'}`} value="SEPARATE" checked={viewMode === 'SEPARATE'} onChange={() => { setViewMode('SEPARATE'); setLoaded(false); setCombinedReport(null); }}>Rapports séparés</ToggleButton>
              <ToggleButton id={`cash-history-combined-${isManagement ? userId : 'mine'}`} type="radio" variant="outline-primary" name={`cash-history-mode-${isManagement ? userId : 'mine'}`} value="COMBINED" checked={viewMode === 'COMBINED'} onChange={() => { setViewMode('COMBINED'); setLoaded(false); setRows([]); }}>Rapport combiné</ToggleButton>
            </ButtonGroup>
          </Col>
          <Col md={4} lg={2}><Form.Label>Raccourci</Form.Label><Form.Select value={filter.quickRange} onChange={(e) => handleQuickRange(e.target.value)}><option value="TODAY">Aujourd’hui</option><option value="YESTERDAY">Hier</option><option value="">Personnalisé</option></Form.Select></Col>
          <Col md={4} lg={2}><Form.Label>Date début</Form.Label><Form.Control type="date" value={filter.dateFrom} onChange={(e) => setFilter((p) => ({ ...p, dateFrom: e.target.value, quickRange: '' }))} /></Col>
          <Col md={4} lg={2}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={filter.dateTo} onChange={(e) => setFilter((p) => ({ ...p, dateTo: e.target.value, quickRange: '' }))} /></Col>
          {viewMode === 'SEPARATE' && <Col md={4} lg={2}><Form.Label>Rapports par page</Form.Label><Form.Select value={pagination.pageSize} onChange={changePageSize}><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option><option value={200}>200</option></Form.Select></Col>}
        </Row>

        {!loaded ? <div className="text-muted small">Sélectionnez une période et lancez la recherche.</div>
          : viewMode === 'COMBINED' ? <CashCombinedReport report={combinedReport} />
          : <>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2"><div className="small text-muted">{pagination.totalItems.toLocaleString('fr-FR')} rapport(s) trouvé(s)</div>{pagination.totalItems > 0 && <div className="small text-muted">Page {pagination.page} sur {pagination.totalPages}</div>}</div>
            <Table hover responsive><thead><tr><th>Ouverte</th><th>Fermée</th><th>Module</th><th>Écart USD</th><th>Écart HTG</th><th></th></tr></thead><tbody>
              {rows.length === 0 ? <tr><td colSpan={6} className="text-center text-muted">Aucune session clôturée pour ce filtre.</td></tr>
                : rows.map((row) => <tr key={row.id}><td>{row.openedAt ? new Date(row.openedAt).toLocaleString('fr-FR') : '—'}</td><td>{row.closedAt ? new Date(row.closedAt).toLocaleString('fr-FR') : '—'}</td><td>Tous</td><td>{fmt(row.differenceUsd)}</td><td>{fmt(row.differenceHtg)}</td><td><Button size="sm" variant="outline-primary" onClick={() => openDetails(row.id)}>Voir</Button></td></tr>)}
            </tbody></Table>
            {pagination.totalPages > 1 && <div className="d-flex justify-content-center mt-3"><Pagination className="mb-0 flex-wrap">
              <Pagination.First disabled={!pagination.hasPreviousPage || loading} onClick={() => loadSeparate(1, pagination.pageSize)} />
              <Pagination.Prev disabled={!pagination.hasPreviousPage || loading} onClick={() => loadSeparate(pagination.page - 1, pagination.pageSize)} />
              {pageNumbers.map((page, index) => <React.Fragment key={page}>{pageNumbers[index - 1] && page - pageNumbers[index - 1] > 1 && <Pagination.Ellipsis disabled />}<Pagination.Item active={page === pagination.page} disabled={loading} onClick={() => loadSeparate(page, pagination.pageSize)}>{page}</Pagination.Item></React.Fragment>)}
              <Pagination.Next disabled={!pagination.hasNextPage || loading} onClick={() => loadSeparate(pagination.page + 1, pagination.pageSize)} />
              <Pagination.Last disabled={!pagination.hasNextPage || loading} onClick={() => loadSeparate(pagination.totalPages, pagination.pageSize)} />
            </Pagination></div>}
          </>}
      </Card.Body>
      <CashSessionDetailModal show={detailShow} onHide={() => setDetailShow(false)} session={detailSession} />
    </Card>
  );
}
